import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import LoginPage from './components/auth/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';
import Subjects from './components/academic/Subjects';
import Sections from './components/academic/Sections';
import Mappings from './components/academic/Mappings';
import Timetable from './components/academic/Timetable';
import StudentTimetable from './components/academic/StudentTimetable';
import Attendance from './components/academic/Attendance';
import AttendanceSheet from './components/academic/AttendanceSheet';
import FacultyClasses from './components/academic/FacultyClasses';
import AcademicSetup from './components/academic/AcademicSetup';
import PrincipalDepartments from './components/academic/PrincipalDepartments';
import PrincipalStaffDirectory from './components/academic/PrincipalStaffDirectory';
import PrincipalStudents from './components/academic/PrincipalStudents';
import StudentProfile from './components/academic/StudentProfile';
import MyAttendance from './components/academic/MyAttendance';
import LeaveManagement from './components/academic/LeaveManagement';
import LandingPage from './pages/LandingPage';
import ProtectedRoute from './ProtectedRoute';

import { Users, Activity, Layers, GraduationCap } from 'lucide-react';
import { cn } from './lib/utils';

// Dashboard Home with Real Data
const Dashboard = () => {
    const [stats, setStats] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        fetch('/api/stats')
            .then(res => res.json())
            .then(data => {
                setStats(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
            <div className="h-8 w-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            <p className="text-muted-foreground font-medium">Synchronizing Portal Data...</p>
        </div>
    );

    const cards = [
        { label: 'Total Students', value: stats?.totalStudents || 0, icon: Users, color: 'text-primary' },
        { label: 'Active Faculty', value: stats?.activeFaculty || 0, icon: GraduationCap, color: 'text-emerald-500' },
        { label: 'Departments', value: stats?.departments || 0, icon: Layers, color: 'text-blue-500' },
    ];

    return (
        <div className="space-y-6 animate-in fade-in duration-700">
            <h1 className="text-3xl font-black font-heading text-primary tracking-tight">System Overview</h1>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {cards.map((card, idx) => (
                    <div key={idx} className="bg-card p-6 rounded-3xl shadow-lg shadow-foreground/5 border border-border/50 group hover:scale-[1.02] transition-all">
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-muted-foreground font-bold text-xs uppercase tracking-widest leading-none">{card.label}</h3>
                                <p className={cn("text-5xl font-black mt-3 font-heading", card.color)}>{card.value.toLocaleString()}</p>
                            </div>
                            <div className={cn("p-4 rounded-2xl bg-secondary/50", card.color)}>
                                <card.icon size={24} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
                <div className="bg-card p-8 rounded-[2rem] border border-border/50 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3">
                        <Activity size={20} className="text-primary" />
                        Live Feed
                    </h3>
                    <div className="space-y-6">
                        {(stats?.recentActivity || []).map((activity) => (
                            <div key={activity.id} className="flex items-start gap-4 group">
                                <div className="h-2 w-2 mt-2 rounded-full bg-primary shadow-lg shadow-primary/50 group-hover:scale-150 transition-transform"></div>
                                <div className="flex-1">
                                    <p className="font-semibold text-foreground leading-tight text-sm">{activity.message}</p>
                                    <p className="text-[10px] text-muted-foreground mt-1 font-bold uppercase tracking-wider">{activity.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-primary text-primary-foreground p-8 rounded-[2rem] shadow-2xl shadow-primary/20 relative overflow-hidden group">
                    <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-white/10 blur-[50px] rounded-full group-hover:scale-110 transition-transform duration-700"></div>
                    <h3 className="text-xl font-bold mb-4 font-heading">Portal Status</h3>
                    <p className="text-primary-foreground/80 font-medium leading-relaxed mb-6">
                        HUBVERSE platform is currently managing digital identities for {stats?.totalStudents} students and {stats?.activeFaculty} faculty members across the campus.
                    </p>
                    <div className="flex items-center gap-4 text-xs font-black uppercase tracking-widest bg-white/10 w-fit px-4 py-2 rounded-full border border-white/20">
                        <div className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></div>
                        V3.4 Stable Deployment
                    </div>
                </div>
            </div>
        </div>
    );
};

function App() {
    return (
        <Router>
            <AuthProvider>
                <DataProvider>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/dashboard" element={
                            <ProtectedRoute>
                                <DashboardLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<Dashboard />} />
                            <Route path="subjects" element={<Subjects />} />
                            <Route path="sections" element={<Sections />} />
                            <Route path="mappings" element={<Mappings />} />
                            <Route path="timetable" element={<Timetable />} />
                            <Route path="my-timetable" element={<StudentTimetable />} />
                            <Route path="attendance" element={<Attendance />} />
                            <Route path="attendance/:mappingId" element={<AttendanceSheet />} />
                            <Route path="classes" element={<FacultyClasses />} />
                            <Route path="my-attendance" element={<MyAttendance />} />
                            <Route path="academic" element={<AcademicSetup />} />
                            <Route path="departments-setup" element={<PrincipalDepartments />} />
                            <Route path="staff" element={<PrincipalStaffDirectory />} />
                            <Route path="students" element={<PrincipalStudents />} />
                            <Route path="students/:studentId" element={<StudentProfile />} />
                            <Route path="leaves" element={<LeaveManagement />} />
                        </Route>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </DataProvider>
            </AuthProvider>
        </Router>
    );
}

export default App;
